/**
 * 🚀 BOOTSTRAPPER - The Idioma Club
 * ------------------------------------
 * Coords initialization of core services and feature modules.
 * Extracts logic from app-main.ts to keep it clean.
 */

import { registry } from './registry';
import { Logger } from '@theidiomaclub/logging';
import { LatencyTracker } from '../utils/LatencyTracker';

export class Bootstrapper {
    private static _instance: Bootstrapper;
    private _isInitialized: boolean = false;

    private constructor() { }

    static getInstance(): Bootstrapper {
        if (!this._instance) {
            this._instance = new Bootstrapper();
        }
        return this._instance;
    }

    // Singleton instance access


    /**
     * Critical init — ONLY what React needs to mount.
     * After audit: NO React feature uses registry repos/use cases.
     * This method is intentionally near-empty.
     */
    async initCritical(): Promise<void> {
        if (this._isInitialized) return;
        this._isInitialized = true;

        performance.mark('boot-critical-start');
        Logger.system('Bootstrapper: Starting engine...');
        document.body.classList.add('loaded');
        performance.mark('boot-critical-end');
        performance.measure('Bootstrapper:Critical', 'boot-critical-start', 'boot-critical-end');
        Logger.system('Bootstrapper: Critical path complete (near-zero)');
    }

    /**
     * Background init — loads repos, use cases, and late-core modules.
     * Runs via requestIdleCallback so it NEVER blocks React mount.
     *
     * 🛡️ 3-UNIVERSE CODE SPLITTING (Performance Law — Mar 2026):
     * - Student:  public repos + student repos (~11 modules)
     * - Teacher:  public repos ONLY (~2 modules) — teacher features use Supabase directly
     * - Admin:    public repos + admin repos + use cases (~30+ modules)
     *
     * @param userRole — from JWT claims (app_metadata.user_role), already available in app-main.ts
     */
    initBackground(userRole?: string): void {
        const loadAll = async (): Promise<void> => {
            const role = userRole ?? 'student';
            const isAdmin = ['super_admin', 'admin'].includes(role);
            const isTeacher = role === 'teacher';
            const moduleCount = isTeacher ? 2 : isAdmin ? 30 : 11;

            // 📊 TELEMETRY: Role-aware performance tracking
            performance.mark(`boot-bg-${role}-start`);
            const tracker = new LatencyTracker(`Bootstrapper:Background:${role}`);

            // 🚀 Yield helper — breaks long tasks into smaller chunks for better main-thread responsiveness
            const yieldToMain = (): Promise<void> => {
                return new Promise(resolve => {
                    if ('scheduler' in window && 'yield' in (window as unknown as { scheduler: { yield: () => Promise<void> } }).scheduler) {
                        (window as unknown as { scheduler: { yield: () => Promise<void> } }).scheduler.yield().then(() => resolve());
                    } else {
                        requestAnimationFrame(() => resolve());
                    }
                });
            };

            try {
                // Quien estudia lo necesita, sea cual sea su rol — ver boot-contract.test.ts.
                const RecordAttempt = await import('../application/usecases/lesson/RecordExerciseAttempt');
                if (RecordAttempt.RecordExerciseAttempt) registry.register('RecordExerciseAttempt', new RecordAttempt.RecordExerciseAttempt());
                /* EL UNIVERSO DEL ADMIN YA NO SE ARRANCA AQUÍ. El panel se mudó a
                   `apps/backoffice`, que trae su propio arranque y su propio
                   registro; esta app ya no tiene sus repositorios ni sus casos
                   de uso que registrar. Un admin que entre aquí carga lo mismo
                   que un alumno y salta a `/admin/`. */
                if (isTeacher) {
                    // 🎓 Teacher universe: public repos ONLY (~2 modules)
                    await this._registerPublicRepositories();
                    await yieldToMain();
                    Logger.system('Bootstrapper: Teacher universe loaded (public repos only)');
                } else {
                    // 📚 Student universe: public + student repos in parallel (~11 modules)
                    await Promise.all([
                        this._registerPublicRepositories(),
                        this._registerStudentRepositories(),
                    ]);
                    await yieldToMain();
                    Logger.system('Bootstrapper: Student universe loaded (public + student repos)');
                }

                performance.mark(`boot-bg-${role}-end`);
                performance.measure(`Bootstrapper:Background:${role}`, `boot-bg-${role}-start`, `boot-bg-${role}-end`);
                tracker.finish({ success: true, role, modules: moduleCount });
            } catch (error) {
                Logger.error('Bootstrapper: Background init failure', error);
                tracker.finish({ success: false, role, modules: moduleCount, error });
            }

            // Late-core: sentry, notifications — needed for ALL users
            this._scheduleLateCore();
        };

        // ⏱️ 2s timeout (was 5s) — if main thread hasn't yielded by then, something else is wrong
        if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
            window.requestIdleCallback(() => loadAll(), { timeout: 2000 });
        } else {
            setTimeout(() => loadAll(), 100);
        }
    }

    /**
     * @deprecated Use initCritical() + initBackground() instead.
     * Kept for backward compatibility — calls both sequentially.
     */
    async init(): Promise<void> {
        await this.initCritical();
        this.initBackground();
    }

    private async _registerPublicRepositories(): Promise<void> {
        // Essential repositories used by ALL roles (public screens + universal features)
        const [SubRepoModule, NotifRepo] = await Promise.all([
            import('../infrastructure/repositories/SupabaseSubscriptionRepository'),
            import('../infrastructure/repositories/NotificationRepository')
        ]);

        this._safeRegister(SubRepoModule, 'SubscriptionRepository', false, 'SupabaseSubscriptionRepository');
        this._safeRegister(NotifRepo, 'NotificationRepository');

        Logger.system('Bootstrapper: Public repos loaded (2 modules)');
    }

    /**
     * 📚 Student repos — loaded for ALL authenticated users.
     * These power: Map, Lessons, Profile, Clan, Rank features.
     * ~8 module imports vs ~30+ in the old monolithic _registerRepositories.
     */
    private async _registerStudentRepositories(): Promise<void> {
        const [
            LessonRepoModule, SessionRepoModule, ProfileRepoModule,
            CohortRepoModule, SkillRepo, ProgressRepo, CourseRepo, ClanRepo
        ] = await Promise.all([
            import('../infrastructure/repositories/LessonRepository'),
            import('../infrastructure/repositories/SessionRepository'),
            import('../infrastructure/repositories/ProfileRepository'),
            import('../infrastructure/repositories/CohortRepository'),
            import('../infrastructure/repositories/SkillMasteryRepository'),
            import('../infrastructure/repositories/UserProgressRepository'),
            import('../infrastructure/repositories/CourseRepository'),
            import('../infrastructure/repositories/ClanRepository')
        ]);

        this._safeRegister(LessonRepoModule, 'LessonRepository', true);
        this._safeRegister(SessionRepoModule, 'SessionRepository', true);
        this._safeRegister(ProfileRepoModule, 'ProfileRepository');
        this._safeRegister(CohortRepoModule, 'CohortRepository');
        this._safeRegister(SkillRepo, 'SkillMasteryRepository');
        this._safeRegister(ProgressRepo, 'UserProgressRepository');
        this._safeRegister(CourseRepo, 'CourseRepository', true);
        this._safeRegister(ClanRepo, 'ClanRepository');

        Logger.system('Bootstrapper: Student repos loaded (8 modules)');
    }


    private _safeRegister(module: Record<string, unknown>, registryKey: string, preferDefault = false, classNameOverride?: string): void {
        const className = classNameOverride || registryKey;
        const ClassObj = preferDefault ? (module.default || module[className]) : (module[className] || module.default);

        if (ClassObj && typeof ClassObj === 'function') {
            const ValidClass = ClassObj as new () => unknown;
            registry.register(registryKey, new ValidClass());
        } else {
            console.error(`❌ Bootstrapper: Could not find class for ${registryKey} in module`, Object.keys(module));
        }
    }



    /**
     * Schedule non-critical modules to keep main thread free.
     *
     * 🛡️ ROLE-BASED SPLIT:
     * - Universal (ALL users): sentry, error-handler, EventTracker, eventBus, NotificationService
     * - Admin-only: navigation, sync-manager, utils, notify, AssetPrefetcher
     */
    private _scheduleLateCore(): void {
        const loadLateModules = async () => {
            Logger.system('Bootstrapper: Staggering late-core modules...');
            try {
                // 🚀 PERF: Yield helper — breaks long tasks for browser responsiveness
                const yieldFrame = (): Promise<void> => {
                    return new Promise(resolve => {
                        if ('scheduler' in window && 'yield' in (window as unknown as { scheduler: { yield: () => Promise<void> } }).scheduler) {
                            (window as unknown as { scheduler: { yield: () => Promise<void> } }).scheduler.yield().then(() => resolve());
                        } else {
                            requestAnimationFrame(() => resolve());
                        }
                    });
                };

                // ── BATCH 1: Universal modules (parallel where safe) ──
                // sentry → error-handler MUST be sequential (error-handler may use Sentry)
                // EventTracker + eventBus are independent → load in parallel with sentry chain
                await Promise.all([
                    // Chain: sentry then error-handler
                    import('./sentry.ts').then(() => import('./error-handler.ts')),
                    // Independent: EventTracker + eventBus in parallel
                    import('./EventTracker').then(m => {
                        registry.register('EventTracker', m.eventTrackerInstance);
                    }),
                    import('./eventBus').then(m => {
                        registry.register('EventBus', m.eventBus);
                    }),
                ]);

                // Yield — give browser a frame to handle other work
                await yieldFrame();

                // NotificationService loads AFTER eventBus (may depend on it)
                await import('./NotificationService').then(async m => {
                    const service = new m.NotificationService();
                    registry.register('NotificationService', service);
                    // Wrap init in rAF to avoid forced reflow
                    requestAnimationFrame(() => service.init());
                });

                Logger.system('Bootstrapper: Late-core modules completed');
            } catch (e) {
                Logger.error('Bootstrapper: Error loading late modules', e);
            }
        };

        if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
            window.requestIdleCallback(() => loadLateModules(), { timeout: 10000 });
        } else {
            setTimeout(loadLateModules, 5000);
        }
    }



}

export const bootstrapper = Bootstrapper.getInstance();

if (!registry.get('Bootstrapper')) {
    registry.register('Bootstrapper', bootstrapper);
}
